/** Constants **/
const DOM_CONTENT_LOADED_EVENT = 'DOMContentLoaded';

/** Styles **/
import './css/blazor.css';
import '../../CoinLedger.UI.Blazor.Components/wwwroot/css/tailwind.css';
import '../../CoinLedger.UI.Blazor.Components/wwwroot/css/loaders/wave.css';

/** Polyfills **/
// [WEB-1143] - intersection observer is used by the Virtualize component in Blazor core.
import '../../CoinLedger.UI.Blazor.Components/wwwroot/lib/intersection-observer';

/** Scripts **/
import './js/individual/common/checkout.js';
import './js/billing/affiliate_tracking.js';
import './js/individual/common/connectWallet.js';
import './js/individual/common/typeformManager.js';
import './js/silentLogin/silentLogin.js';
// import * as zendesk from './js/integrations/zendesk.js';
import './js/integrations/clarity.js';
import './js/integrations/sentry.js';
import '../../CoinLedger.UI.Blazor.Components/wwwroot/js/windowHelpers.js';
import '../../CoinLedger.UI.Blazor.Components/wwwroot/js/elementReference.js';
import '../../CoinLedger.UI.Blazor.Components/wwwroot/js/chartsJSInterop.js';
import '../../CoinLedger.UI.Blazor.Components/wwwroot/js/downloadHelper.js';
import '../../CoinLedger.UI.Blazor.Components/wwwroot/js/isValidBrowser.js';
import '../../CoinLedger.UI.Blazor.Components/wwwroot/js/intersectionObserverManager.js';
import '../../CoinLedger.UI.Blazor.Components/wwwroot/js/pageHideInterop.js';

/** Components **/
import '../../CoinLedger.UI.Blazor.Components/Common/VirtualScroll/VirtualScroll'
import '../../CoinLedger.UI.Blazor.Components/Common/Inputs/Select/Select'

/** Vendors **/
import '../../CoinLedger.UI.Blazor.Components/wwwroot/lib/chart.js';
import '../../CoinLedger.UI.Blazor.Components/wwwroot/lib/chartjs-adapter-luxon';
import '../../CoinLedger.UI.Blazor.Components/wwwroot/lib/chartjs-plugin-annotation';
import '../../CoinLedger.UI.Blazor.Components/wwwroot/lib/luxon';
import '../../CoinLedger.UI.Blazor.Components/wwwroot/lib/bowser';

/** Service Workers **/
window.addEventListener(DOM_CONTENT_LOADED_EVENT, () => {
  if ('serviceWorker' in navigator) {
    const version = document.querySelector("meta[name='coinledger-version']")?.getAttribute("content");
    navigator.serviceWorker.register(`blazor-worker.js?version=${version}`);
    navigator.serviceWorker.ready.then(registration => {
      registration.active.postMessage("Boot");
    });
  }
});

/* Get User */
window.getUser = async () => {
  const USER_ENDPOINT = '/bff/user';

  // Fetch user claims
  return fetch(USER_ENDPOINT, {
    headers: {
      'X-CSRF': '1'
    }
  });
}

/* Login Loop Detection - Time-windowed tracking with sessionStorage */
const LoginLoopDetector = {
  MAX_ATTEMPTS: 5,
  TIME_WINDOW_MS: 60000, // 60 seconds
  STORAGE_KEY: 'cl_auth_failures',

  /**
   * Show the login loop error UI
   */
  showErrorUI() {
    document.body.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; min-height: 100vh; background: #1e293b; color: white; font-family: system-ui, -apple-system, sans-serif;">
        <div style="max-width: 600px; padding: 2rem; text-align: center;">
          <h1 style="font-size: 2rem; margin-bottom: 1rem; font-weight: 600;">Unable to Sign In</h1>
          <p style="margin-bottom: 1.5rem; color: #cbd5e1; line-height: 1.6;">
            We're experiencing authentication issues. Our team has been notified.
          </p>
          <button
            onclick="window.LoginLoopDetector.tryAgain()"
            style="background: #3b82f6; color: white; padding: 0.75rem 1.5rem; border-radius: 0.375rem; border: none; font-weight: 500; cursor: pointer; font-size: 1rem; margin-bottom: 1rem;"
            onmouseover="this.style.background='#2563eb'"
            onmouseout="this.style.background='#3b82f6'">
            Try Again
          </button>
          <p style="color: #94a3b8; font-size: 0.875rem;">
            Please contact support if this persists.
          </p>
        </div>
      </div>
    `;
  },

  /**
   * Check if we're in a login loop
   * @returns {boolean} true if loop detected
   */
  check() {
    const recentFailures = this._getRecentFailures();
    return recentFailures.length >= this.MAX_ATTEMPTS;
  },

  /**
   * Record an authentication failure in the time window
   * Automatically expires entries older than TIME_WINDOW_MS
   * Loop detection happens at page load via check()
   * @param {string} reason - "bff_401" (initial load) or "api_401" (runtime API call)
   */
  recordFailure(reason) {
    const failures = this._getRecentFailures();

    // Add new failure
    failures.push({
      timestamp: Date.now(),
      reason: reason
    });

    // Save to sessionStorage
    this._saveFailures(failures);

    console.log(`Auth failure recorded (${reason}): ${failures.length}/${this.MAX_ATTEMPTS}`);
  },

  /**
   * Clear all failure records and navigate to login
   */
  tryAgain() {
    console.log('User requested Try Again - clearing loop state and navigating to login');
    this.reset();

    // Build clean return URL without returnUrl param to prevent nesting
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete('returnUrl');
    const returnUrl = currentUrl.toString();

    // Redirect to login
    const LOGIN_ENDPOINT = '/bff/login';
    window.location.href = `${LOGIN_ENDPOINT}?returnUrl=${encodeURIComponent(returnUrl)}`;
  },

  /**
   * Clear all failure records
   */
  reset() {
    try {
      sessionStorage.removeItem(this.STORAGE_KEY);
      console.log('Auth failure tracking cleared');
    } catch (e) {
      console.warn('Failed to clear auth failures from sessionStorage:', e);
    }
  },

  /**
   * Get recent failures within the time window
   * Automatically filters out expired entries (older than TIME_WINDOW_MS)
   * @returns {Array<{timestamp: number, reason: string}>} Array of failure entries
   */
  _getRecentFailures() {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      if (!stored) return [];

      const failures = JSON.parse(stored);
      if (!Array.isArray(failures)) return [];

      // Filter to only recent failures within time window
      const cutoff = Date.now() - this.TIME_WINDOW_MS;
      const recentFailures = failures.filter(f => f.timestamp > cutoff);

      // Save back filtered list (cleanup old entries)
      if (recentFailures.length !== failures.length) {
        this._saveFailures(recentFailures);
      }

      return recentFailures;
    } catch (e) {
      console.warn('Failed to read auth failures from sessionStorage:', e);
      return [];
    }
  },

  /**
   * Save failures to sessionStorage
   * @param {Array} failures - Array of failure entries
   */
  _saveFailures(failures) {
    try {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(failures));
    } catch (e) {
      console.warn('Failed to save auth failures to sessionStorage:', e);
    }
  }
};

// Expose to window for C# JS Interop
window.LoginLoopDetector = LoginLoopDetector;

/** Initial Load Auth Check **/
window.addEventListener(DOM_CONTENT_LOADED_EVENT, async (event) => {
  const LOGIN_ENDPOINT = '/bff/login';
  const LOGOUT_ENDPOINT = '/bff/logout';
  const INVITATION_ID = 'invitationId';
  const ACCEPTED_INVITATION_ID = "acceptInvitationId";
  const SUPPORT_URL_PATH = '/support/';
  const REFERRER = "referrer";
  const PARTNER_TOKEN = "partnerToken";
  const BYPASS_PARTNER_LOGOUT = "bypassPartnerLogout";
  const USER_TYPE = "userType";
  const USER_EMAIL_HINT = "userEmailHint";

  console.log('Attempting to resolve user claims on application load.');

  // Check for redirect loop - stops if detected
  if (LoginLoopDetector.check()) {
    console.error('Login loop detected - stopping to prevent further issues.');
    LoginLoopDetector.showErrorUI();
    return;
  }

  // Get current user
  const params = new URLSearchParams(window.location.search);
  let userClaimsResponse;

  try {
    userClaimsResponse = await getUser();
  } catch (error) {
    console.error('Failed to fetch user claims:', error);
    return; // Don't count network errors as login attempts
  }

  // Note: We do NOT reset the counter on 200 status because API 401s might occur
  // later during Blazor runtime, and we need to track those across page reloads

  if (userClaimsResponse.status === 200
    && params.has(REFERRER)
    && params.has(PARTNER_TOKEN)
    && !params.has(BYPASS_PARTNER_LOGOUT)) {

    // Force logout on specific partner scenarios
    console.log('Partner scenario detected. Forcing logout redirect...');

    // Get Logout Url
    const claims = await userClaimsResponse.json();
    const logoutUrlClaim = claims.filter(o => o.type === "bff:logout_url")[0];

    // Build Return Url
    const returnUrlObj = new URL(window.location.href);
    returnUrlObj.searchParams.delete('returnUrl');
    returnUrlObj.searchParams.set(BYPASS_PARTNER_LOGOUT, 'true');
    const returnUrl = returnUrlObj.toString();

    // Redirect To Url
    window.location.href = logoutUrlClaim === undefined
      ? `${LOGOUT_ENDPOINT}?returnUrl=${encodeURIComponent(returnUrl)}`
      : `${logoutUrlClaim.value}&returnUrl=${encodeURIComponent(returnUrl)}`;
  }
  else if (userClaimsResponse.status === 401) {
    // Navigate to login
    console.log('Unauthenticated - redirecting to login.');

    // Record auth failure (loop will be detected on next page load)
    LoginLoopDetector.recordFailure('bff_401');

    // Clean returnUrl - prevent nested returnUrl params
    console.log(`Initial URL: ${window.location.href}`);
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete('returnUrl');

    let acrValues = '';

    /**
     * ACR Values
     */

    // Include invitation in ACR values
    if (params.has(INVITATION_ID)) {
      acrValues += "&" + ACCEPTED_INVITATION_ID + "=" + params.get(INVITATION_ID);
    }

    // When the referrer query param exists for an unauthenticated user, take them directly to the signup page.
    if (params.has(REFERRER)) {

      // Include 'referrer' in ACR values
      acrValues += `&referrer=${params.get(REFERRER)}`;

      // Include 'partnerToken' in ACR values
      if (params.has(PARTNER_TOKEN)) {
        acrValues += `&partnerToken=${params.get(PARTNER_TOKEN)}`;
      }

      // Include MetaMask Portfolio 'accounts' and 'networks' in ACR values
      if (params.has('accounts') && params.has('networks')) {
        acrValues += `&accounts=${params.get('accounts')}`;
        acrValues += `&networks=${params.get('networks')}`;
      }

      // Include 'userType' in ACR values
      acrValues += '&signup=';
      if (params.has(USER_TYPE)) {
        // User type signup
        acrValues += params.get(USER_TYPE);
      } else {
        // Default signup
        acrValues += 'individual';
      }

      // Include 'userEmailHint' in ACR values
      if (params.has(USER_EMAIL_HINT)) {
        acrValues += `&userEmailHint=${params.get(USER_EMAIL_HINT)}`;
      }
    }

    // Add IDP in ACR values
    if (window.location.href.indexOf(SUPPORT_URL_PATH) > -1) {
      acrValues += "&idp=InternalGoogle";
    }

    /**
     * Return Url Query Parameters
     */

    // Include 'bypassPartnerLogout' query param on partner scenario
    if (params.has(REFERRER) &&
      params.has(PARTNER_TOKEN) &&
      !params.has(BYPASS_PARTNER_LOGOUT)) {
      console.log('CoinStats referrer & partner token detected. Including logout bypass in url...');
      currentUrl.searchParams.set(BYPASS_PARTNER_LOGOUT, 'true');
    }

    // Build final returnUrl after all modifications
    const returnUrl = currentUrl.toString();

    // Redirect To Final Url
    let finalLoginUrl = acrValues === ''
      ? `${LOGIN_ENDPOINT}?returnUrl=${encodeURIComponent(returnUrl)}`
      : `${LOGIN_ENDPOINT}?returnUrl=${encodeURIComponent(returnUrl)}${acrValues}`;

    console.log(`Final Return Url: ${returnUrl}`);
    console.log(`Navigating To: ${finalLoginUrl}`);

    window.location.href = finalLoginUrl;
  }
});
